import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js/max"; // max version for phone type detection
import { useState } from "react";

interface UsePhoneValidationReturn {
  phoneError: string;
  validatePhoneNumber: (phoneValue: string, defaultCountry?: string) => boolean;
  clearPhoneError: () => void;
}

interface UsePhoneValidationWithKeyReturn {
  phoneErrors: Record<string, string>;
  validatePhoneNumber: (
    phoneValue: string,
    key: string,
    defaultCountry?: string,
  ) => boolean;
  clearPhoneError: (key: string) => void;
  getPhoneError: (key: string) => string;
}

/**
 * Helper function to get detailed error message for phone validation
 */
const getDetailedPhoneError = (
  phoneValue: string,
  countryParam?: string,
): string | null => {
  // First check: Length validation (most specific)
  const lengthResult = validatePhoneNumberLength(
    phoneValue,
    countryParam as any,
  );

  if (lengthResult) {
    const errorMessages: Record<string, string> = {
      INVALID_COUNTRY: "Invalid country code. Please select a valid country.",
      NOT_A_NUMBER:
        "Please enter only numbers. Remove any letters or special characters.",
      TOO_SHORT: "Phone number is too short. Please enter the complete number.",
      TOO_LONG:
        "Phone number is too long. Please check and remove extra digits.",
      INVALID_LENGTH:
        "Phone number has incorrect length for the selected country.",
    };
    return errorMessages[lengthResult] || "Invalid phone number format.";
  }

  // Second check: Format validation
  if (!isPossiblePhoneNumber(phoneValue, countryParam as any)) {
    // Try to parse and get more context
    const phoneNumber = parsePhoneNumberFromString(
      phoneValue,
      countryParam as any,
    );
    if (!phoneNumber) {
      return "Phone number format is invalid. Please check the country code and number.";
    }
    return "Phone number format doesn't match the selected country.";
  }

  // Third check: Full validation
  if (!isValidPhoneNumber(phoneValue, countryParam as any)) {
    const phoneNumber = parsePhoneNumberFromString(
      phoneValue,
      countryParam as any,
    );
    if (phoneNumber) {
      const detectedCountry = phoneNumber.country;
      if (detectedCountry) {
        return `This appears to be a phone number from ${detectedCountry}, but it's not valid. Please check the number.`;
      }
    }
    return "This phone number cannot be verified. Please check and try again.";
  }

  // Fourth check: Detect landlines and non-mobile types (Twilio compatibility)
  const phoneNumber = parsePhoneNumberFromString(
    phoneValue,
    countryParam as any,
  );

  if (phoneNumber) {
    const phoneType = phoneNumber.getType();

    if (phoneType === "FIXED_LINE") {
      return "This appears to be a landline. Messages cannot be sent to landline numbers.";
    }

    if (phoneType === "TOLL_FREE") {
      return "This is a toll-free number. Messages cannot be sent to toll-free numbers.";
    }

    if (phoneType === "VOIP") {
      return "This appears to be a VoIP number. Message delivery may not be reliable.";
    }

    // Only MOBILE, PAGER, or undefined pass through without warning
  }

  return null; // No error
};

/**
 * Custom hook for validating phone numbers using libphonenumber-js
 *
 * @returns {UsePhoneValidationReturn} Object containing:
 *   - phoneError: Current error message (empty string if no error)
 *   - validatePhoneNumber: Function to validate a phone number
 *   - clearPhoneError: Function to manually clear the error
 *
 * @example
 * ```tsx
 * const { phoneError, validatePhoneNumber } = usePhoneValidation();
 *
 * const handlePhoneChange = (value: string) => {
 *   validatePhoneNumber(value, 'US');
 * };
 *
 * <MuiPhoneNumber
 *   value={phone}
 *   onChange={handlePhoneChange}
 *   error={!!phoneError}
 *   helperText={phoneError}
 * />
 * ```
 */
export const usePhoneValidation = (): UsePhoneValidationReturn => {
  const [phoneError, setPhoneError] = useState<string>("");

  const validatePhoneNumber = (
    phoneValue: string,
    defaultCountry?: string,
  ): boolean => {
    if (!phoneValue) {
      setPhoneError("Phone number is required");
      return false;
    }

    try {
      // If phone number starts with +, it has country code - don't pass defaultCountry
      // This allows validation to auto-detect the country from the number
      const countryParam = phoneValue.startsWith("+")
        ? undefined
        : (defaultCountry as any);

      // Get detailed error message
      const errorMessage = getDetailedPhoneError(phoneValue, countryParam);

      if (errorMessage) {
        setPhoneError(errorMessage);
        return false;
      }

      setPhoneError("");
      return true;
    } catch (error) {
      setPhoneError(
        "Unable to validate phone number. Please check the format and try again.",
      );
      return false;
    }
  };

  const clearPhoneError = () => {
    setPhoneError("");
  };

  return {
    phoneError,
    validatePhoneNumber,
    clearPhoneError,
  };
};

/**
 * Custom hook for validating multiple phone numbers using libphonenumber-js
 * Useful when you have multiple phone inputs and need to track errors separately
 *
 * @returns {UsePhoneValidationWithKeyReturn} Object containing:
 *   - phoneErrors: Object with error messages keyed by field identifier
 *   - validatePhoneNumber: Function to validate a phone number with a key
 *   - clearPhoneError: Function to clear error for a specific key
 *   - getPhoneError: Function to get error for a specific key
 *
 * @example
 * ```tsx
 * const { phoneErrors, validatePhoneNumber, getPhoneError } = usePhoneValidationWithKey();
 *
 * contactPoints.map((contact, index) => (
 *   <MuiPhoneNumber
 *     value={contact.value}
 *     onChange={(value) => validatePhoneNumber(value, index.toString(), 'US')}
 *     error={!!getPhoneError(index.toString())}
 *     helperText={getPhoneError(index.toString())}
 *   />
 * ))
 * ```
 */
export const usePhoneValidationWithKey =
  (): UsePhoneValidationWithKeyReturn => {
    const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({});

    const validatePhoneNumber = (
      phoneValue: string,
      key: string,
      defaultCountry?: string,
    ): boolean => {
      if (!phoneValue) {
        setPhoneErrors((prev) => ({
          ...prev,
          [key]: "Phone number is required",
        }));
        return false;
      }

      try {
        // If phone number starts with +, it has country code - don't pass defaultCountry
        // This allows validation to auto-detect the country from the number
        const countryParam = phoneValue.startsWith("+")
          ? undefined
          : (defaultCountry as any);

        // Get detailed error message
        const errorMessage = getDetailedPhoneError(phoneValue, countryParam);

        if (errorMessage) {
          setPhoneErrors((prev) => ({
            ...prev,
            [key]: errorMessage,
          }));
          return false;
        }

        // Clear error for this key
        setPhoneErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
        return true;
      } catch (error) {
        setPhoneErrors((prev) => ({
          ...prev,
          [key]:
            "Unable to validate phone number. Please check the format and try again.",
        }));
        return false;
      }
    };

    const clearPhoneError = (key: string) => {
      setPhoneErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    };

    const getPhoneError = (key: string): string => {
      return phoneErrors[key] || "";
    };

    return {
      phoneErrors,
      validatePhoneNumber,
      clearPhoneError,
      getPhoneError,
    };
  };
